import { TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { capturePHEvents } from '../analytics/posthog.analytics';

import { createAssignment, updateAssignment } from '../service/assignment.service';
import { uploadFile } from '../service/upload.file.service';
import palette from '../theme/palette';
import FileUploadContainer from './FileUploadContainer';
import DownloadFileContainer from './DownloadFileContainer';
import { ConvertToBase64 } from '../helper/commonHelper';
import { PH_NEW_ASSIGNMENT_CREATED } from '../utils/posthog.eventNames';
import Modal from './common/Modal';

const useStyle = makeStyles({
  characterContainer: {
    // textAlign: 'end',
    display: 'flex',
    justifyContent: 'space-between',
    color: palette.grey[500]
  },
  dateContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 15
  }
});

export default function AddOrUpdateAssignment({ open, onClose, editObj }) {
  const classes = useStyle();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const classData = useSelector((state) => state.classes.selectedClass);
  const { id: classId, orgId } = classData;
  const [assignmentName, setAsgnName] = useState('');
  const [description, setDesc] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [submissionDate, setSubmissionDate] = useState(null);
  const [selectedPaper, setSelectedPaper] = useState(null);

  const [nameError, setNameError] = useState(false);
  const [startDateError, setStartDateError] = useState(false);
  const [subDateError, setSubDateError] = useState(false);
  const [paperSelectionError, setPSError] = useState(false);
  const [isAsgnUploading, setAsgnUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const checkForError = () => {
    const isName = assignmentName.trim() !== '';
    const isStartDate = startDate !== null;
    const isSubDate = submissionDate !== null;
    const isPaper = selectedPaper !== null;

    setNameError(assignmentName.trim() === '');
    setStartDateError(startDate === null);
    setSubDateError(submissionDate === null && startDate !== submissionDate);
    setPSError(selectedPaper === null);

    return isName && isStartDate && isSubDate && isPaper;
  };

  const handleClose = () => {
    setAsgnName('');
    setDesc('');
    setStartDate(null);
    setSubmissionDate(null);
    setSelectedPaper(null);

    setNameError(false);
    setStartDateError(false);
    setSubDateError(false);
    setPSError(false);
    onClose();
  };

  useEffect(() => {
    if (editObj) {
      setAsgnName(editObj.name);
      setDesc(editObj?.description ?? '');
      setStartDate(editObj.startDateTime);
      setSubmissionDate(editObj.endDateTime);
      setSelectedPaper({ name: editObj.fileURL });
    }
  }, [editObj]);

  const updateAsgnData = async (assignmentObj) => {
    const res = await updateAssignment(editObj.id, assignmentObj);
    if (res) {
      setAsgnUploading(false);
      handleClose();
    } else {
      setAsgnUploading(false);
    }
  };

  const onUpdate = async () => {
    if (checkForError()) {
      setAsgnUploading(true);
      const assignmentObj = {
        name: assignmentName,
        startDateTime: new Date(startDate).toISOString(),
        endDateTime: new Date(submissionDate).toISOString(),
        duration: moment(submissionDate).diff(moment(startDate), 'minutes')
      };
      if (description) assignmentObj.description = description;
      // The file is updated
      // eslint-disable-next-line
      if (selectedPaper && selectedPaper.hasOwnProperty('type')) {
        const base64 = await ConvertToBase64(selectedPaper);
        const fileObj = {
          base64,
          fileType: selectedPaper.type,
          fileName: selectedPaper.name,
          uploadFor: 'assignment',
          userId: currentUser.id,
          orgId: orgId?._id ?? orgId
        };
        const fileRes = await uploadFile(classId, fileObj);
        if (fileRes) {
          assignmentObj.fileURL = fileRes.fileURL;
          updateAsgnData(assignmentObj);
        }
      } else {
        updateAsgnData(assignmentObj);
      }
    }
  };

  const onSubmit = async () => {
    if (checkForError()) {
      setAsgnUploading(true);
      const base64 = await ConvertToBase64(selectedPaper);
      const fileObj = {
        base64,
        fileType: selectedPaper.type,
        fileName: selectedPaper.name,
        uploadFor: 'assignment',
        userId: currentUser.id,
        orgId: orgId?._id ?? orgId
      };
      const fileRes = await uploadFile(classId, fileObj);
      if (fileRes) {
        const assignmentObj = {
          orgId: orgId?._id ?? orgId,
          name: assignmentName,
          type: 'upload',
          fileURL: fileRes.fileURL,
          startDateTime: new Date(startDate).toISOString(),
          endDateTime: new Date(submissionDate).toISOString(),
          isSolutionUploaded: false,
          duration: moment(submissionDate).diff(moment(startDate), 'minutes')
        };
        if (description) assignmentObj.description = description;
        const asgnRes = await createAssignment(classId, assignmentObj);
        if (asgnRes) {
          setAsgnUploading(false);
          capturePHEvents(PH_NEW_ASSIGNMENT_CREATED, {
            assignmentId: asgnRes?.id ?? asgnRes?._id,
            classId,
            assignmentName
          });
          handleClose();
        } else {
          setAsgnUploading(false);
        }
      } else {
        enqueueSnackbar('Please try again later', {
          variant: 'error'
        });
        setAsgnUploading(false);
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add New Assignment"
      cancelText="Cancel"
      actionText={editObj ? 'Update' : 'Save'}
      onAction={editObj ? () => onUpdate() : () => onSubmit()}
      onCancel={() => handleClose()}
      isBtnLoading={isAsgnUploading}
      isCancelDisabled={isAsgnUploading}
    >
      <TextField
        autoFocus
        margin="dense"
        label="Assignment Name *"
        placeholder="Example: Class test, mid test..."
        type="text"
        fullWidth
        error={nameError}
        name="assignmentName"
        value={assignmentName}
        onChange={(e) => setAsgnName(e.target.value)}
        helperText={nameError ? 'Please select date' : ''}
      />
      <TextField
        fullWidth
        value={description}
        onChange={(e) => setDesc(e.target.value)}
        margin="dense"
        placeholder="Description (optional)"
        multiline
        inputProps={{
          maxLength: 100
        }}
        minRows={5}
        maxRows={5}
      />
      <div className={classes.characterContainer}>
        <span> Make point write in comma separated</span>
        <span>{`Character ${description.length}/100`}</span>
      </div>
      <div className={classes.dateContainer}>
        <LocalizationProvider fullWidth dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Start date"
            value={startDate}
            minDate={new Date()}
            onChange={(newValue) => setStartDate(newValue)}
            renderInput={(params) => (
              <TextField
                fullWidth
                error={startDateError}
                helperText={startDateError ? 'Please select date' : ''}
                style={{ width: '48%' }}
                margin="dense"
                {...params}
              />
            )}
          />
        </LocalizationProvider>
        <LocalizationProvider fullWidth dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Submission Date"
            value={submissionDate}
            minDate={startDate}
            onChange={(newValue) => {
              setSubmissionDate(newValue);
            }}
            renderInput={(params) => (
              <TextField
                fullWidth
                error={subDateError}
                helperText={subDateError ? 'Please select date' : ''}
                style={{ width: '48%' }}
                margin="dense"
                {...params}
              />
            )}
          />
        </LocalizationProvider>
      </div>
      <Typography style={{ marginTop: 20, marginBottom: 5 }} fontSize={14}>
        Upload assignment questions paper
      </Typography>
      <Typography color="#00000099" style={{ marginBottom: 20 }} fontSize={12}>
        Select one file at a time.You can use PDF or image.
      </Typography>
      {selectedPaper ? (
        <DownloadFileContainer
          title={selectedPaper.name}
          onCancelClick={() => setSelectedPaper(null)}
        />
      ) : (
        <FileUploadContainer onFileChange={(file) => setSelectedPaper(file)} />
      )}
      {paperSelectionError && <Typography color="red">Please assignment paper</Typography>}
    </Modal>
  );
}

AddOrUpdateAssignment.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  editObj: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    fileURL: PropTypes.string,
    id: PropTypes.string
  })
};
